import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, BarChart3, Activity, Server } from 'lucide-react';
import { StatCard } from '../common/StatCard';
import { Spinner } from '../common/Spinner';
import { JobStatusBoard } from './JobStatusBoard';
import { LiveActivityFeed } from './LiveActivityFeed';
import { useJobStore } from '../../stores/jobStore';
import { useActivityStore } from '../../stores/activityStore';
import { useVpsStore } from '../../stores/vpsStore';
import { usePolling } from '../../hooks/usePolling';

function getTrendDirection(rate: number): 'up' | 'down' | 'neutral' {
  if (rate > 90) return 'up';
  if (rate > 70) return 'neutral';
  return 'down';
}

function getTokenStatus(tokens: number): 'success' | 'warning' | 'info' {
  if (tokens > 1_000_000) return 'warning';
  if (tokens > 500_000) return 'info';
  return 'success';
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`;
  return String(tokens);
}

export default function DashboardPage() {
  const navigate = useNavigate();

  const jobs = useJobStore((s) => s.jobs);
  const stats = useJobStore((s) => s.stats);
  const usage = useJobStore((s) => s.usage);
  const recentRuns = useJobStore((s) => s.recentRuns);
  const fetchJobs = useJobStore((s) => s.fetchJobs);
  const fetchStats = useJobStore((s) => s.fetchStats);
  const fetchRecentRuns = useJobStore((s) => s.fetchRecentRuns);

  const activityEvents = useActivityStore((s) => s.events);
  const activityLoading = useActivityStore((s) => s.loading);
  const fetchEvents = useActivityStore((s) => s.fetchEvents);

  const vpsStatus = useVpsStore((s) => s.status);
  const fetchVpsStatus = useVpsStore((s) => s.fetchStatus);

  const fetchJobData = useCallback(async () => {
    await Promise.all([fetchJobs(), fetchStats(), fetchRecentRuns()]);
  }, [fetchJobs, fetchStats, fetchRecentRuns]);

  usePolling(fetchJobData, 30_000);
  usePolling(fetchEvents, 30_000);
  usePolling(fetchVpsStatus, 60_000);

  const isLoading = !stats && jobs.length === 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  const runningNow = stats?.runningNow ?? 0;
  const todayRuns = stats?.todayRuns ?? 0;
  const todaySuccessRate = stats?.todaySuccessRate ?? 0;
  const vpsConnected = vpsStatus?.connected ?? false;

  // Prefer ccusage data (accurate, reads JSONL session files) over heartbeat.db
  const hasUsage = usage?.available ?? false;
  const todayCost = hasUsage ? (usage?.totalCost ?? 0) : (stats?.todayCost ?? 0);
  const todayTokens = hasUsage
    ? (usage?.totalTokens ?? 0)
    : (stats?.todayInputTokens ?? 0) + (stats?.todayOutputTokens ?? 0);
  const cacheReadTokens = hasUsage ? (usage?.cacheReadTokens ?? 0) : 0;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          label="Active Jobs"
          value={runningNow}
          icon={<Briefcase />}
          status={runningNow > 0 ? 'info' : 'success'}
        />
        <StatCard
          label="Today's Runs"
          value={todayRuns}
          icon={<BarChart3 />}
          trend={todayRuns > 0 ? getTrendDirection(todaySuccessRate) : undefined}
          trendValue={todayRuns > 0 ? `${todaySuccessRate}% success` : undefined}
        />
        <StatCard
          label="Token Usage"
          value={`~$${todayCost.toFixed(2)}`}
          subtitle={`${formatTokens(todayTokens)} tokens${cacheReadTokens ? ` (${formatTokens(cacheReadTokens)} cached)` : ''}`}
          icon={<Activity />}
          status={getTokenStatus(todayTokens)}
        />
        <StatCard
          label="VPS Health"
          value={vpsConnected ? 'Online' : 'Offline'}
          icon={<Server />}
          status={vpsConnected ? 'success' : 'error'}
          onClick={() => navigate('/vps')}
        />
      </div>

      {/* Job Status + Activity Feed */}
      <div className="flex flex-col lg:flex-row gap-4">
        <JobStatusBoard jobs={jobs} recentRuns={recentRuns} />
        <LiveActivityFeed
          events={activityEvents}
          loading={activityLoading}
          onRefresh={fetchEvents}
        />
      </div>
    </div>
  );
}
